import { daysBetween } from '~/calculations/calendar'
import { PHASE_LABELS, type PhaseType, SUGGESTED_WEEKLY_CHANGE_PERCENT } from '~/models/phase'
import { defaultProjectName } from '~/models/project'
import type {
  ConditionBand,
  CurrentCondition,
  NotPlanned,
  RouteDuration,
  RouteOutcome,
} from '~/models/routeRecommendation'
import type { Confidence, EvidenceItem } from '~/models/rulesOpinion'
import type { ApproximateMonth, CalendarDate } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import { DEFAULT_ROUTE_ENGINE_THRESHOLDS, type RouteEngineThresholds } from '../thresholds'
import { ROUTE_ENGINE_VERSION } from '../version'
import type { RouteInputSnapshot, TrainingExperience } from './snapshot'
import { applyTrainingFrequencyFloor, chooseRoute } from './table'

/**
 * The route-recommendation engine (D-062, D-071).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE USER DESCRIBES A DESTINATION. THIS DETERMINES THE ROUTE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The second deterministic engine, under every constraint the first one has:
 * pure, no clock, no storage, no formatting, reference date and thresholds
 * injected, and every numeric threshold in `rules/thresholds.ts` carrying the
 * decision that authorised it.
 *
 * **AI is not involved and no future version may make it so** (D-008, D-062).
 * The order is fixed: Project Sola's deterministic recommendation first, then
 * an optional manual second opinion on a recommendation it has already made.
 *
 * Four constraints bind it, and it is worth being precise about how each is
 * actually enforced, because two of them are not runtime branches:
 *
 *  1. **It never applies itself.** It returns a value. Creating the project is
 *     an explicit user action (D-020), and the layering guard keeps `rules/`
 *     from reaching storage at all, so this cannot be violated from here.
 *  2. **It never recommends a phase whose ruleset is unimplemented** (D-043).
 *     All five are implemented, so a runtime check would be an unreachable
 *     branch — and an unreachable guard cannot be shown to fail. `route.test.ts`
 *     drives every outcome across every band and checks each result against
 *     `SELECTABLE_PHASE_TYPES`, which fails the moment a registry entry flips.
 *  3. **It cannot invent target ranges.** It reads the approved D-065 defaults.
 *  4. **It cannot emit `high` confidence.** A first-day snapshot has no
 *     adherence, recovery or strength history — the evidence `high` requires
 *     (D-060, D-066) — so the ceiling is structural rather than a judgement.
 *
 * It also names what Project Sola does not plan, rather than omitting it.
 */
export function recommendRoute(
  snapshot: RouteInputSnapshot,
  thresholds: RouteEngineThresholds = DEFAULT_ROUTE_ENGINE_THRESHOLDS,
): RouteOutcome {
  const stamp = { engineVersion: ROUTE_ENGINE_VERSION, evaluatedAt: snapshot.evaluatedAt }

  /* Waist-to-height is the primary evidence and there is no substitute for it.
     Without a waist median or a height there is no route and no confidence, and
     this is never stored or exported as a recommendation (D-071). */
  const { waistCm, heightCm } = snapshot
  if (waistCm === undefined || heightCm === undefined || heightCm <= 0) {
    return {
      outcome: 'routeUnavailable',
      reason: 'noWaistToHeight',
      missingEvidence: [
        ...(waistCm === undefined ? ['A waist measurement, as the median of three readings'] : []),
        ...(heightCm === undefined || heightCm <= 0 ? ['Your height'] : []),
      ],
      ...stamp,
    }
  }

  const waistToHeight = waistCm / heightCm
  const band = bandFor(waistToHeight, thresholds)
  const weeksToEvent = wholeWeeksBetween(snapshot.reference, snapshot.eventDate)

  const chosen = applyTrainingFrequencyFloor(
    chooseRoute({
      desiredOutcome: snapshot.desiredOutcome,
      band,
      weeksToEvent,
      eventHasEnoughLeadTime:
        weeksToEvent !== undefined && weeksToEvent >= thresholds.route.minWeeksForAestheticPrep,
    }),
    snapshot.weeklySessions,
    thresholds.route.minWeeklySessionsForLeanBulk,
  )

  const condition: CurrentCondition = {
    waistToHeight,
    band,
    waistCm,
    heightCm,
    estimatedBodyFatPercent: snapshot.estimatedBodyFatPercent,
  }
  const confidence = assessRouteConfidence(condition, thresholds)

  return {
    outcome: 'route',
    recommendedPhase: chosen.phase,
    desiredOutcome: snapshot.desiredOutcome,
    condition,
    reasoning: chosen.reasoning,
    redirect: chosen.redirect,
    suggestedProject: {
      name: defaultProjectName(snapshot.targetDate),
      startDate: snapshot.reference,
      targetDate: snapshot.targetDate,
    },
    duration: durationOf(snapshot.reference, snapshot.targetDate),
    primaryObjective: PRIMARY_OBJECTIVES[chosen.phase],
    /* The approved D-065 defaults, read rather than invented. The phase record
       will store its own copy and the weekly engine judges against that. */
    weeklyTargetPercent: SUGGESTED_WEEKLY_CHANGE_PERCENT[chosen.phase],
    successCriteria: successCriteriaFor(chosen.phase, snapshot.trainingExperience),
    confidence: confidence.level,
    confidenceReason: confidence.reason,
    evidence: evidenceFor(condition),
    missingEvidence: missingEvidenceFor(snapshot),
    notPlanned: NOT_PLANNED,
    ruleId: chosen.ruleId,
    ...stamp,
  }
}

/**
 * Which side of the two boundaries the reading falls on.
 *
 * Ordered high band first so the two comparisons cannot both apply. The
 * boundary itself belongs to the band it names: D-071 says fat loss takes
 * priority **at or above** 0.50, and is no longer recommended **below** 0.45.
 *
 * **Exported at C7.0 so there is exactly one implementation of 0.50.** The AI
 * payload carries a condition band, and the alternative was three lines of
 * comparison in a service — which is how a published threshold quietly acquires
 * a second, drifting copy. Exporting it does **not** make the route engine
 * reusable for anything else: this answers only "which side of the boundaries",
 * and D-062's rule that nothing may feed the route engine is untouched, since
 * this is a read of a threshold rather than a route recommendation.
 */
export function bandFor(waistToHeight: number, thresholds: RouteEngineThresholds): ConditionBand {
  if (waistToHeight >= thresholds.route.fatLossPriorityWaistToHeight) return 'fatLossPriority'
  if (waistToHeight < thresholds.route.leanEnoughWaistToHeight) return 'leanEnough'
  return 'middle'
}

/**
 * Whole weeks from `from` to `to`, rounded **down**.
 *
 * Down rather than to nearest, because the only place this gates anything is
 * the minimum lead time for an aesthetic preparation, and the conservative
 * direction there is to find less time rather than more. Fifty-five days is
 * seven weeks and six days; calling it eight would start a preparation D-071
 * says there is not room for.
 */
export function wholeWeeksBetween(
  from: CalendarDate,
  to: CalendarDate | undefined,
): number | undefined {
  if (to === undefined) return undefined
  return Math.floor(daysBetween(from, to) / 7)
}

/**
 * How long the project runs — the user's own horizon, restated.
 *
 * Not a projection: they gave the target date. Stated to the month per D-068,
 * because everything this app says about the future is said to the month.
 */
function durationOf(reference: CalendarDate, targetDate: CalendarDate): RouteDuration {
  return {
    weeks: Math.max(0, Math.floor(daysBetween(reference, targetDate) / 7)),
    approximateMonth: monthOf(targetDate),
  }
}

function monthOf(date: CalendarDate): ApproximateMonth {
  const iso = unwrapDate(date)
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) }
}

/**
 * How sure the engine is about the side of the boundary the user is on.
 *
 * **`high` is unreachable and that is the point** (D-062, D-060). A first-day
 * snapshot has no adherence, no recovery and no strength history, which is
 * exactly the evidence `high` requires. The reason says so out loud rather than
 * leaving the ceiling to be inferred.
 *
 * `low` when the reading sits within measurement noise of a boundary: a waist
 * the protocol itself tolerates a 0.7 cm spread on cannot confidently be placed
 * on one side of a line it is 0.2 cm from. The noise figure is the approved one
 * (D-016), converted to ratio terms against this user's own height rather than
 * a second number being chosen.
 */
function assessRouteConfidence(
  condition: CurrentCondition,
  thresholds: RouteEngineThresholds,
): { readonly level: Confidence; readonly reason: string } {
  const noiseInRatio = thresholds.waistNoiseCm / condition.heightCm
  const boundaries = [
    thresholds.route.fatLossPriorityWaistToHeight,
    thresholds.route.leanEnoughWaistToHeight,
  ]
  const nearBoundary = boundaries.some(
    (boundary) => Math.abs(condition.waistToHeight - boundary) <= noiseInRatio,
  )

  if (nearBoundary) {
    return {
      level: 'low',
      reason:
        'Your waist measurement sits close to the point where Project Sola would recommend a different route. A measurement or two more will settle which side of it you are on.',
    }
  }

  return {
    level: 'moderate',
    reason:
      'Based on one set of measurements. Project Sola has no record yet of how you respond to training or to a change in intake, which is what a higher confidence would need.',
  }
}

const PRIMARY_OBJECTIVES: Readonly<Record<PhaseType, string>> = {
  fatLoss: 'Reduce fat at a controlled rate while keeping muscle, strength and recovery.',
  maintenance: 'Hold weight steady, recover, and keep strength and routine.',
  leanBulk: 'Gain weight slowly to support muscle and performance, with the waist controlled.',
  miniCut: 'Lose a modest amount of fat over a short, explicit period.',
  aestheticPrep: 'Look leaner for a specific date, staying conservative and health-first.',
}

/**
 * What the project is aiming at, stated at setup (D-071).
 *
 * **An aim, never a verdict.** L-012 asked for per-route success criteria and
 * D-062 forbids any score, grade, percentage or streak in the completion
 * summary — irreconcilable if these were pass/fail. So they are shown here, at
 * the start, and the completion summary still reports only counts and coverage.
 * Nothing may compare a finished project against this list.
 *
 * The figures quoted are the phase's own approved range (D-065). No new number
 * is introduced: a criterion like "waist down 4–6 cm" would be a threshold
 * nobody approved, dressed as encouragement.
 */
function successCriteriaFor(
  phase: PhaseType,
  experience: TrainingExperience | undefined,
): readonly string[] {
  const range = SUGGESTED_WEEKLY_CHANGE_PERCENT[phase]
  const rate = `Weekly weight change staying inside ${range.min}% to ${range.max}% of body weight`

  const shared = [rate, 'Weekly reviews completed, so there is enough evidence to judge the weeks']

  const strength =
    experience === 'beginner'
      ? 'Strength on your sentinel lifts holding or improving'
      : 'Strength on your sentinel lifts holding'

  switch (phase) {
    case 'fatLoss':
    case 'miniCut':
      return [...shared, 'Waist trending down across measurements', strength]
    case 'leanBulk':
      return [...shared, 'Waist staying roughly where it is while weight rises', strength]
    case 'maintenance':
      return [...shared, 'Waist and weight both steady', strength]
    case 'aestheticPrep':
      return [...shared, 'Waist trending down without the rate accelerating', strength]
  }
}

function evidenceFor(condition: CurrentCondition): readonly EvidenceItem[] {
  return [
    {
      kind: 'waist',
      statement: 'Waist measured as the median of three readings.',
      value: condition.waistCm,
      unit: 'cm',
    },
    {
      kind: 'waist',
      statement: 'Waist as a proportion of height.',
      value: condition.waistToHeight,
      unit: 'ratio',
    },
    ...(condition.estimatedBodyFatPercent === undefined
      ? []
      : [
          {
            kind: 'waist' as const,
            statement:
              'Navy circumference estimate for men — a longitudinal personal-tracking estimate, not a measurement.',
            value: condition.estimatedBodyFatPercent,
            unit: '%',
          },
        ]),
  ]
}

function missingEvidenceFor(snapshot: RouteInputSnapshot): readonly string[] {
  return [
    ...(snapshot.estimatedBodyFatPercent === undefined
      ? ['A neck measurement, which would add the Navy circumference estimate']
      : []),
    ...(snapshot.weeklySessions === undefined
      ? ['How many sessions a week you realistically train']
      : []),
    /* Always true on the first day, and worth saying: it is why confidence
       cannot reach `high` and why the first few weekly reviews matter. */
    'Any record of how you respond to training or to a change in intake',
  ]
}

/**
 * Named as unavailable, not silently omitted (D-062).
 *
 * The premise of this feature is the four-way question people ask themselves,
 * and one of those four routes is deferred entirely (D-012, F-001). A user who
 * has heard of recomposition and sees no mention of it will assume the app
 * forgot, so it says plainly that it does not plan one, and why.
 */
const NOT_PLANNED: readonly NotPlanned[] = [
  {
    capability: 'General recomposition — losing fat and gaining muscle at once',
    reason: `Project Sola does not plan recomposition phases. Judging one needs evidence it does not collect, so it would be guessing. ${PHASE_LABELS.maintenance} with consistent training is the nearest thing it will recommend.`,
  },
]
